import { GridColumn } from '@entity-framework/entity-grid/state/entity-grid-column';
import { columnFormatters } from '@utils/display-formatters';
import { CellClassParams } from '@ag-grid-community/core';
import { ContributionRecordError } from './state/pension-contribution-employee.model';

const hasErrors = (params: CellClassParams): boolean => {
  return params.data?.errors && params.data.errors.length > 0;
};

const getRowErrorClass = (params: CellClassParams): string => {
  return hasErrors(params) ? 'error-row-cell' : '';
};

export const pensionContributionsGridColumns = (): GridColumn[] => [
  {
    propertyName: 'employmentId',
    headerLabel: 'Employment ID',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1,
      minWidth: 120,
      cellClass: getRowErrorClass
    }
  },
  {
    propertyName: 'employeeName',
    headerLabel: 'Employee name',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 2,
      minWidth: 150,
      cellClass: getRowErrorClass
    }
  },
  {
    propertyName: 'errors',
    headerLabel: 'Error description',
    propertyType: 'text',
    sortable: false,
    formatForDisplay: (value: ContributionRecordError[]) =>
      value && value.length > 0 ? value.map((e) => `• ${e.errorDescription}`).join('\n') : '',
    colDefOverrides: {
      flex: 3,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      cellClass: (params: CellClassParams) => {
        const baseClass = getRowErrorClass(params);
        return hasErrors(params) ? `${baseClass} error-text` : baseClass;
      },
      cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.4' }
    }
  },
  {
    propertyName: 'periodEarnings',
    headerLabel: 'Period earnings',
    propertyType: 'number',
    sortable: true,
    alignRight: true,
    formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
    colDefOverrides: {
      flex: 1.5,
      minWidth: 130,
      type: 'numericColumn',
      cellClass: getRowErrorClass
    }
  },
  {
    propertyName: 'employeeContribution',
    headerLabel: 'Employee contribution',
    propertyType: 'number',
    sortable: true,
    alignRight: true,
    formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
    colDefOverrides: {
      flex: 1.8,
      minWidth: 150,
      type: 'numericColumn',
      cellClass: getRowErrorClass
    }
  },
  {
    propertyName: 'employerContribution',
    headerLabel: 'Employer contribution',
    propertyType: 'number',
    sortable: true,
    alignRight: true,
    formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
    colDefOverrides: {
      flex: 1.8,
      minWidth: 150,
      type: 'numericColumn',
      cellClass: getRowErrorClass
    }
  },
  {
    propertyName: 'total',
    headerLabel: 'Total',
    propertyType: 'number',
    sortable: true,
    alignRight: true,
    formatForDisplay: (value: number) => columnFormatters.currency(value, 'GBP'),
    textBold: () => true,
    colDefOverrides: {
      flex: 1.2,
      minWidth: 110,
      type: 'numericColumn',
      cellClass: getRowErrorClass
    }
  },
  {
    propertyName: 'pensionGroup',
    headerLabel: 'Pension group',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string | null) => value || '-',
    colDefOverrides: {
      flex: 1.5,
      minWidth: 130,
      cellClass: getRowErrorClass
    }
  }
];
